import { all, call, put, takeLatest } from "redux-saga/effects";
import { getErrorMessage } from "utils/sagasErrorHandler";
import {
    exportMetricDataCollectionStatus,
    getMetricDataCollectionStatus,
    getMetricDataCollectionStatusError,
    updateMetricDataCollectionStatus,
} from "modules/Metric/services/MetricDataCollectionStatus/actions";
import {defaultSortDirection, defaultSortField} from "modules/Metric/services/constants";
import {getMetricDataCollectionStatusRequest} from "modules/Metric/services/MetricDataCollectionStatus/api";
import {ITEMS_PER_PAGE} from "constants/general";

function* handleGetMetricDataCollectionStatus ({ payload }) {
    try {
        const { metric_data_collection_status } = yield call(getMetricDataCollectionStatusRequest, payload);

        let { limit, offset, sort_field, sort_direction } = payload;

        sort_field = sort_field ? sort_field : defaultSortField;
        sort_direction = sort_direction ? sort_direction : defaultSortDirection;

        metric_data_collection_status.sort((a, b) => {
            if (a[sort_field] === undefined) {
                return sort_direction === "asc" ? -1 : 1;
            }
            if (b[sort_field] === undefined) {
                return sort_direction === "asc" ? 1 : -1;
            }
            if (a[sort_field] < b[sort_field]) {
                return sort_direction === "asc" ? -1 : 1;
            }
            if (a[sort_field] > b[sort_field]) {
                return sort_direction === "asc" ? 1 : -1;
            }
            return 0;
        });

        const start = offset ? parseInt(offset) : 0;
        const end = start + (limit ? parseInt(limit) : parseInt(ITEMS_PER_PAGE));

        let filteredData = filterCollectionStatusData(metric_data_collection_status, payload);

        yield put(updateMetricDataCollectionStatus({
            metricData: filteredData.slice(start, end),
            count: filteredData.length,
            isLoading: false
        }));
    } catch (error) {
        yield put(getMetricDataCollectionStatusError());
        getErrorMessage(error);
    }
}

function* handleExportMetricDataCollectionStatus ({ payload }) {
    try {
        const { metric_data_collection_status } = yield call(getMetricDataCollectionStatusRequest, payload);

        let { sort_field, sort_direction } = payload;

        sort_field = sort_field ? sort_field : defaultSortField;
        sort_direction = sort_direction ? sort_direction : defaultSortDirection;

        metric_data_collection_status.sort((a, b) => {
            if (a[sort_field] === undefined) {
                return sort_direction === "asc" ? -1 : 1;
            }
            if (b[sort_field] === undefined) {
                return sort_direction === "asc" ? 1 : -1;
            }
            if (a[sort_field] < b[sort_field]) {
                return sort_direction === "asc" ? -1 : 1;
            }
            if (a[sort_field] > b[sort_field]) {
                return sort_direction === "asc" ? 1 : -1;
            }
            return 0;
        });

        let filteredData = filterCollectionStatusData(metric_data_collection_status, payload);

        let today = new Date();
        let dd = String(today.getDate()).padStart(2, '0');
        let mm = String(today.getMonth() + 1).padStart(2, '0');
        let yyyy = today.getFullYear();

        let filename = yyyy + '-' + mm + '-' + dd + '_collection_status.csv';
        const csv = convertToCSV(filteredData);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        if (navigator.msSaveBlob) { // For IE 10+
            navigator.msSaveBlob(blob, filename);
        } else {
            const link = document.createElement('a');
            if (link.download !== undefined) {
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', filename);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        }
    } catch (error) {
        yield put(getMetricDataCollectionStatusError());
        getErrorMessage(error);
    }
}

function convertToCSV(filteredData) {
    let csv = '';
    const headers = [
        'Branch',
        'Default Interval',
        'Scraper Status',
        'Scraper Status Change',
        'Scraper Status Broken Since',
        'AVG Scraped Listings',
        'AVG Error Rate',
        'Archiving Status',
        'Archiving Status Change',
        'Archiving Status Broken Since',
        'AVG Archiving Error Rate',
        'Takedown Status',
        'Takedown Status Change',
        'Takedown Status Broken Since',
        'AVG Takedown Error Rate',
    ];
    const header = headers.join(',') + '\n';
    csv += header;
    filteredData.forEach(item => {
        const values = [
            item.branch_name,
            item.default_interval,
            item.scraper_status,
            item.scraper_status_change,
            item.scraper_broken_since,
            item.average_scraped_listings_per_branch,
            item.average_error_rate_per_branch,
            item.screenshot_status,
            item.screenshot_status_change,
            item.screenshot_broken_since,
            item.average_screenshot_error_rate_per_branch,
            item.status_check_status,
            item.status_check_status_change,
            item.status_check_broken_since,
            item.average_status_check_error_rate_per_branch,
        ];
        const row = values.join(',') + '\n';
        csv += row;
    });
    return csv;
}

function filterCollectionStatusData(data, payload) {
    let { general_status, scraper_status, screenshot_status, status_check_status, status_change, branch_name, hide_no_data } = payload;
    return data.filter(item => {
        //by branch name
        let validBranchName = true;
        if (branch_name) {
            validBranchName = `${item.branch_code} ${item.branch_name}`.toLowerCase().includes(branch_name.toLowerCase());
        }
        //generalStatus
        let validGeneralStatus = true;
        if (general_status && general_status === "WORKING") {
            validGeneralStatus = (item.scraper_status === "WORKING" && item.screenshot_status === "WORKING" && item.status_check_status === "WORKING");
        } else if (general_status && general_status === "PARTIALLY_WORKING") {
            validGeneralStatus = (item.scraper_status === "PARTIALLY_WORKING" || item.screenshot_status === "PARTIALLY_WORKING" || item.status_check_status === "PARTIALLY_WORKING");
        } else if (general_status && general_status === "BROKEN") {
            validGeneralStatus = (item.scraper_status === "BROKEN" || item.screenshot_status === "BROKEN" || item.status_check_status === "BROKEN");
        } else if (general_status && general_status === "NO_DATA") {
            validGeneralStatus = (item.scraper_status === "" && item.screenshot_status === "" && item.status_check_status === "");
        }

        //scraperStatus
        let validScraperStatus = true;
        if (scraper_status && scraper_status === "WORKING") {
            validScraperStatus = item.scraper_status === "WORKING";
        } else if (scraper_status && scraper_status === "PARTIALLY_WORKING") {
            validScraperStatus = item.scraper_status === "PARTIALLY_WORKING";
        } else if (scraper_status && scraper_status === "BROKEN") {
            validScraperStatus = item.scraper_status === "BROKEN";
        } else if (scraper_status && scraper_status === "NO_DATA") {
            validScraperStatus = item.scraper_status === "";
        }
        //screenshotStatus
        let validScreenshotStatus = true;
        if (screenshot_status && screenshot_status === "WORKING") {
            validScreenshotStatus = item.screenshot_status === "WORKING";
        } else if (screenshot_status && screenshot_status === "BROKEN") {
            validScreenshotStatus = item.screenshot_status === "BROKEN";
        } else if (screenshot_status && screenshot_status === "NO_DATA") {
            validScreenshotStatus = item.screenshot_status === "";
        }
        //statusCheckStatus
        let validStatusCheckStatus = true;
        if (status_check_status && status_check_status === "WORKING") {
            validStatusCheckStatus = item.status_check_status === "WORKING";
        } else if (status_check_status && status_check_status === "PARTIALLY_WORKING") {
            validStatusCheckStatus = item.status_check_status === "PARTIALLY_WORKING";
        } else if (status_check_status && status_check_status === "BROKEN") {
            validStatusCheckStatus = item.status_check_status === "BROKEN";
        } else if (status_check_status && status_check_status === "NO_DATA") {
            validStatusCheckStatus = item.status_check_status === "";
        }
        //status change
        let validStatusChange = true;
        if (status_change && status_change === "BECOME_WORKING") {
            validStatusChange = (item.scraper_status_change === "BECOME_WORKING" || item.screenshot_status_change === "BECOME_WORKING" || item.status_check_status_change === "BECOME_WORKING");
        } else if (status_change && status_change === "BECOME_BROKEN") {
            validStatusChange = (item.scraper_status_change === "BECOME_BROKEN" || item.screenshot_status_change === "BECOME_BROKEN" || item.status_check_status_change === "BECOME_BROKEN");
        } else if (status_change && status_change === "NO_CHANGE") {
            validStatusChange = (item.scraper_status_change === undefined && item.screenshot_status_change === undefined && item.status_check_status_change === undefined);
        }
        //hide no data
        let validHideNoData = true;
        if (hide_no_data !== undefined && hide_no_data === "true") {
            validHideNoData = (
                item.scraper_status !== "" ||
                item.screenshot_status !== "" ||
                item.status_check_status !== ""
            );
        }

        return (validGeneralStatus && validBranchName && validScraperStatus && validScreenshotStatus && validStatusCheckStatus && validStatusChange && validHideNoData);
    });
}

// Watchers
export function* metricDataCollectionStatusSagas() {
    yield all([
        yield takeLatest(getMetricDataCollectionStatus, handleGetMetricDataCollectionStatus),
        yield takeLatest(exportMetricDataCollectionStatus, handleExportMetricDataCollectionStatus),
    ]);
}
